import { createElement } from "react";
import {
  siWhatsapp,
  siWechat,
  siTripadvisor,
  siTiktok,
  siThreads,
  siTelegram,
  siSnapchat,
  siReddit,
  siPinterest,
  siYoutube,
  siX,
  siInstagram,
  siFacebook,
  siDiscord,
} from "simple-icons";

// lucide-react 1.x removed brand glyphs (Instagram, Facebook, ...). We restore
// the common social brands from simple-icons, wrapped so they accept the same
// props lucide icons do (size, color). Icons are imported by name (not via a
// namespace import) so the bundler tree-shakes the 3000+ icon package down to
// only what we use. Keys are the icon_map values Strapi stores. Brands
// simple-icons has dropped for trademark reasons (LinkedIn, Twitter)
// intentionally fall through to the DynamicIcon fallback.
const BRAND_TO_SIMPLE_ICON = {
  Instagram: siInstagram,
  Facebook: siFacebook,
  X: siX,
  Youtube: siYoutube,
  YouTube: siYoutube,
  Tiktok: siTiktok,
  TikTok: siTiktok,
  Whatsapp: siWhatsapp,
  WhatsApp: siWhatsapp,
  Telegram: siTelegram,
  Pinterest: siPinterest,
  Snapchat: siSnapchat,
  Threads: siThreads,
  Wechat: siWechat,
  WeChat: siWechat,
  Discord: siDiscord,
  Reddit: siReddit,
  Tripadvisor: siTripadvisor,
  TripAdvisor: siTripadvisor,

  // The values the CMS actually stores. "Music" is what lucide called TikTok before it dropped its
  // brand set, and "Twitter" predates the rebrand — both resolve to a real mark, so neither should
  // have been falling through to a generic link glyph.
  Music: siTiktok,
  music: siTiktok,
  Twitter: siX,
  twitter: siX,
};

/* simple-icons removed LinkedIn (and Twitter) for trademark reasons, so the mark is drawn here from
 * primitives rather than a path string: the badge, the "i" dot, the "i" stem and the "n".
 */
const EXTRA_BRAND_ICONS = {
  LinkedIn: {
    title: "LinkedIn",
    shapes: [
      [
        "rect",
        {
          x: 2,
          y: 2,
          width: 20,
          height: 20,
          rx: 3,
          fill: "none",
          stroke: "currentColor",
          strokeWidth: 2,
        },
      ],
      ["circle", { cx: 7.2, cy: 7.6, r: 1.4 }],
      ["rect", { x: 6, y: 10.2, width: 2.4, height: 7.4 }],
      [
        "path",
        {
          d: "M10.6 10.2h2.3v1.1a2.9 2.9 0 0 1 2.5-1.3c1.9 0 3 1.2 3 3.4v4.2h-2.4v-3.8c0-1-.4-1.6-1.3-1.6-.8 0-1.4.6-1.4 1.6v3.8h-2.4z",
        },
      ],
    ],
  },
};
EXTRA_BRAND_ICONS.Linkedin = EXTRA_BRAND_ICONS.LinkedIn;
EXTRA_BRAND_ICONS.linkedin = EXTRA_BRAND_ICONS.LinkedIn;

const buildBrandIcon = (icon) => (props) => {
  const size = props.size || 24;
  return createElement(
    "svg",
    {
      role: "img",
      viewBox: "0 0 24 24",
      width: size,
      height: size,
      fill: props.color || "currentColor",
      ...props,
    },
    createElement("title", null, icon.title),
    createElement("path", { d: icon.path }),
  );
};

const buildShapeIcon = (icon) => (props) => {
  const size = props.size || 24;
  return createElement(
    "svg",
    {
      role: "img",
      viewBox: "0 0 24 24",
      width: size,
      height: size,
      fill: props.color || "currentColor",
      ...props,
    },
    createElement("title", null, icon.title),
    ...icon.shapes.map(([tag, attrs]) =>
      createElement(tag, {
        key: `${tag}-${attrs.d || attrs.x || attrs.cx}`,
        ...attrs,
      }),
    ),
  );
};

export const getBrandIcon = (iconName) => {
  const icon = BRAND_TO_SIMPLE_ICON[iconName];
  if (icon) return buildBrandIcon(icon);

  const shaped = EXTRA_BRAND_ICONS[iconName];
  if (shaped) return buildShapeIcon(shaped);

  return null;
};
